import { announce } from "@kovernow/flows/announce";
import { createResetPasswordFlow, type ResetFlow, type ResetState } from "@kovernow/flows/auth";
import { Button } from "@kovernow/react-ui/components/button";
import { useEffect, useMemo, useState, useSyncExternalStore, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useServices } from "../core/app-context";
import { useMutationGate } from "../core/mutation-gate";
import { useT } from "../core/use-t";
import { FormField } from "../components/FormField";
import { GatedButton } from "../components/GatedButton";
import { OtpStep } from "../components/OtpStep";
import { Page } from "../components/Page";
import { PasswordField } from "../components/PasswordField";
import { Recovery } from "../components/Recovery";
import { providerKey } from "./SignUpScreen";

const STEP_TITLES: Record<ResetState["step"], string> = {
  email: "auth.reset.title",
  otp: "auth.otp.title",
  password: "auth.reset.passwordTitle",
  provider: "auth.reset.providerTitle",
  unknown: "auth.reset.unknownTitle",
  done: "auth.reset.title",
};

/** Reset password (W-1): rendered only from the flows reset machine. */
export function ResetPasswordScreen({ flowOverride }: { flowOverride?: ResetFlow }) {
  const services = useServices();
  const [params] = useSearchParams();
  const t = useT();
  const navigate = useNavigate();
  const flow = useMemo(
    () =>
      flowOverride ??
      createResetPasswordFlow({
        api: services.authApi,
        session: services.session,
        initialEmail: params.get("email") ?? undefined,
        recovery: { hasMessage: (k) => services.i18n.exists(k) },
      }),
    // Seeded once per visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flowOverride, services],
  );
  const state = useSyncExternalStore(flow.subscribe, flow.getState);
  const provider = t(providerKey(state.providerId));

  useEffect(() => {
    if (state.step === "done") {
      announce(t("auth.reset.done"));
      navigate("/", { replace: true });
    }
  }, [state.step, navigate, t]);

  const toSignIn = () => navigate(`/sign-in?email=${encodeURIComponent(state.email)}`);

  return (
    <Page
      titleKey={STEP_TITLES[state.step]}
      flowKey="flows.resetPassword"
      focusKey={state.step === "email" ? undefined : state.step}
      className="mx-auto w-full max-w-md px-4 py-8"
    >
      <div aria-live="polite">
        {state.error ? (
          <Recovery
            live={false}
            action={state.error}
            onRetry={() => flow.dismissError()}
            handlers={{ resend_code: () => void flow.resendOtp(), sign_in: toSignIn }}
          />
        ) : null}
      </div>
      <div className="mt-4">
        {state.step === "email" || state.step === "done" ? <EmailStep flow={flow} state={state} /> : null}
        {state.step === "otp" ? (
          <OtpStep
            intro={t("auth.reset.otpIntro", { email: state.email })}
            otpResetCount={state.otpResetCount}
            busy={state.busy}
            error={state.fieldErrors.otp ? t(state.fieldErrors.otp) : undefined}
            resendAvailableAt={state.resendAvailableAt}
            onSubmit={(code) => void flow.submitOtp(code)}
            onResend={() => void flow.resendOtp()}
            onBack={() => flow.back()}
            backLabel={t("auth.otp.useDifferentEmail")}
          />
        ) : null}
        {state.step === "password" ? <PasswordStep flow={flow} state={state} /> : null}
        {state.step === "provider" ? (
          <div className="flex flex-col gap-6">
            <p>{t("auth.reset.providerBody", { email: state.email, provider })}</p>
            <Button type="button" variant="cta" size="touch" onClick={toSignIn}>
              {t("auth.reset.signInWith", { provider })}
            </Button>
            <Button type="button" variant="link" className="self-start px-0" onClick={() => flow.back()}>
              {t("auth.reset.tryAnother")}
            </Button>
          </div>
        ) : null}
        {state.step === "unknown" ? (
          <div className="flex flex-col gap-6">
            <p>{t("auth.reset.unknownBody", { email: state.email })}</p>
            <Button asChild variant="cta" size="touch">
              <Link to="/sign-up">{t("auth.reset.createAccount")}</Link>
            </Button>
            <Button type="button" variant="link" className="self-start px-0" onClick={() => flow.back()}>
              {t("auth.reset.tryAnother")}
            </Button>
          </div>
        ) : null}
      </div>
    </Page>
  );
}

function EmailStep({ flow, state }: { flow: ResetFlow; state: ResetState }) {
  const t = useT();
  const gate = useMutationGate();
  const [email, setEmail] = useState(state.email);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (gate.blocked || state.busy) return;
    void flow.submitEmail(email);
  };
  return (
    <div className="flex flex-col gap-6">
      <p className="text-muted-foreground">{t("auth.reset.intro")}</p>
      <form noValidate onSubmit={submit} className="flex flex-col gap-4">
        <FormField
          label={t("auth.reset.email")}
          name="email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => email && flow.validateField("email", email)}
          error={state.fieldErrors.email ? t(state.fieldErrors.email) : undefined}
        />
        <GatedButton type="submit" variant="cta" busy={state.busy} label={t("auth.reset.submit")} />
      </form>
      <p className="text-sm">
        <Link to="/sign-in" className="inline-flex min-h-11 items-center font-semibold text-primary underline">
          {t("auth.reset.backToSignIn")}
        </Link>
      </p>
    </div>
  );
}

function PasswordStep({ flow, state }: { flow: ResetFlow; state: ResetState }) {
  const t = useT();
  const gate = useMutationGate();
  const [password, setPassword] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (gate.blocked || state.busy) return;
    void flow.submitPassword(password);
  };
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      {/* Lets password managers save the new password against the account. */}
      <input type="hidden" name="username" autoComplete="username" value={state.email} />
      <PasswordField
        label={t("auth.reset.password")}
        name="new-password"
        autoComplete="new-password"
        hint={t("auth.reset.passwordHint")}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onBlur={() => password && flow.validateField("password", password)}
        error={state.fieldErrors.password ? t(state.fieldErrors.password) : undefined}
      />
      <GatedButton type="submit" variant="cta" busy={state.busy} label={t("auth.reset.savePassword")} />
    </form>
  );
}
